/**
 * Container Issues API Hooks
 *
 * React Query hooks for fetching and managing container issues.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface ContainerIssueMetadata {
    source?: string;
    document_id?: string;
    document_name?: string;
    document_type?: string;
    missing_doc_type?: string;
    [key: string]: unknown;
}

export interface ContainerIssue {
    issue_id: string;
    issue_type: string;
    issue_source: string | null;
    title: string;
    description: string | null;
    severity: 'critical' | 'high' | 'medium' | 'low';
    priority: string | null;
    resolution_status: string;
    blocks_shipment: boolean;
    risk_contribution: number | null;
    issue_metadata: ContainerIssueMetadata | null;
    created_at: string | null;
}

export interface ContainerIssuesResponse {
    container_id: string;
    issues: ContainerIssue[];
    count: number;
}

export interface UpdateIssueStatusRequest {
    resolution_status: string;
    status_change_reason?: string;
}

export interface UpdateIssueStatusResponse {
    success: boolean;
    issue_id: string;
    old_status: string;
    new_status: string;
    activity_created: boolean;
}

// Resolution status options
export const RESOLUTION_STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'pending_info', label: 'Pending Info' },
    { value: 'waiting_on_external', label: 'Waiting on External' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'false_positive', label: 'False Positive' },
    { value: 'wont_fix', label: "Won't Fix" },
];

// Active statuses (for badge count)
export const ACTIVE_STATUSES = ['open', 'acknowledged', 'in_progress', 'pending_info', 'waiting_on_external'];

// ============================================================================
// Query Keys
// ============================================================================

export const CONTAINER_ISSUES_QUERY_KEYS = {
    all: ['container-issues'] as const,
    lists: () => [...CONTAINER_ISSUES_QUERY_KEYS.all, 'list'] as const,
    list: (containerId: string) => [...CONTAINER_ISSUES_QUERY_KEYS.lists(), containerId] as const,
    detail: (issueId: string) => [...CONTAINER_ISSUES_QUERY_KEYS.all, 'detail', issueId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch container issues
 *
 * @param containerId - The container UUID
 * @param options - Additional React Query options and filters (e.g. issueType for Document Query)
 */
export function useContainerIssues(
    containerId: string | undefined,
    options?: {
        enabled?: boolean;
        refetchInterval?: number | false;
        /** Filter by issue type (e.g. "documentation_query" for Document Query issues) */
        issueType?: string | null;
    }
) {
    const issueType = options?.issueType ?? undefined;
    return useQuery({
        queryKey: [...CONTAINER_ISSUES_QUERY_KEYS.list(containerId || ''), issueType],
        queryFn: async (): Promise<ContainerIssuesResponse> => {
            if (!containerId) {
                return { container_id: '', issues: [], count: 0 };
            }
            const params = issueType ? { issue_type: issueType } : {};
            return await apiGet<ContainerIssuesResponse>(
                `/container-issues/containers/${containerId}/issues`,
                { params }
            );
        },
        enabled: (options?.enabled ?? true) && !!containerId,
        refetchInterval: options?.refetchInterval ?? false,
        placeholderData: (previousData) => previousData,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to update issue status
 */
export function useUpdateIssueStatus() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            issueId,
            data,
        }: {
            issueId: string;
            containerId: string;
            data: UpdateIssueStatusRequest;
        }): Promise<UpdateIssueStatusResponse> => {
            return await apiPatch<UpdateIssueStatusResponse>(
                `/container-issues/issues/${issueId}/status`,
                data
            );
        },
        onSuccess: (_data, variables) => {
            // Invalidate container issues query to refetch
            queryClient.invalidateQueries({
                queryKey: CONTAINER_ISSUES_QUERY_KEYS.list(variables.containerId),
            });
            // Also invalidate activities to show the new activity
            queryClient.invalidateQueries({
                queryKey: ['activities'],
            });
        },
    });
}

export interface ResetIssuesResponse {
    success: boolean;
    issues_reset: number;
    risk_score: number;
    container_id: string;
}

/**
 * Hook to reset all active issues for a container and recalculate risk.
 */
export function useResetContainerIssues() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (containerId: string): Promise<ResetIssuesResponse> => {
            return await apiPost<ResetIssuesResponse>(
                `/container-issues/containers/${containerId}/issues/reset`
            );
        },
        onSuccess: (_data, containerId) => {
            queryClient.invalidateQueries({
                queryKey: CONTAINER_ISSUES_QUERY_KEYS.list(containerId),
            });
            queryClient.invalidateQueries({ queryKey: ['activities'] });
            queryClient.invalidateQueries({ queryKey: ['containers'] });
            queryClient.invalidateQueries({ queryKey: ['shipment'] });
        },
    });
}
